import {
  DeleteObjectCommand,
  DeleteObjectsCommand,
  GetObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { createHash, randomUUID } from 'node:crypto';
import {
  ConnectionProbeResult,
  ListPage,
  ObjectMetadata,
  PresignedUpload,
  SaveResult,
  StorageAdapter,
  StorageHealthState,
} from './storage-adapter';

export interface S3AdapterOptions {
  bucket: string;
  region: string;
  endpoint?: string;
  accessKeyId: string;
  secretAccessKey: string;
  /** Where objects on this bucket are publicly readable from, no trailing slash. */
  publicBaseUrl?: string;
  pathPrefix?: string;
  /** R2 requires path-style addressing; real S3 prefers virtual-hosted. */
  forcePathStyle?: boolean;
}

/**
 * One adapter for both Cloudflare R2 and Amazon S3.
 *
 * R2 speaks the S3 API, so a second implementation would be two copies of the
 * same code differing only in an endpoint and a region string. The differences
 * that do exist are handled by configuration, not by branching: R2 wants
 * `region: 'auto'`, a per-account endpoint and path-style addressing; S3 wants
 * a real region and no endpoint override.
 *
 * Every S3 error is classified into a `StorageHealthState` rather than
 * surfaced raw — "AccessDenied" and "NoSuchBucket" mean very different things
 * to a merchant fixing their own bucket setup, and the health state is what
 * both the merchant banner and the platform fleet view render.
 */
export class S3CompatibleAdapter implements StorageAdapter {
  private readonly client: S3Client;
  private readonly bucket: string;
  private readonly publicBase?: string;
  private readonly prefix: string;

  constructor(private readonly options: S3AdapterOptions) {
    this.bucket = options.bucket;
    this.publicBase = options.publicBaseUrl?.replace(/\/+$/, '');
    this.prefix = options.pathPrefix ? `${options.pathPrefix.replace(/^\/+|\/+$/g, '')}/` : '';
    this.client = new S3Client({
      region: options.region || 'auto',
      endpoint: options.endpoint || undefined,
      forcePathStyle: options.forcePathStyle ?? Boolean(options.endpoint),
      credentials: {
        accessKeyId: options.accessKeyId,
        secretAccessKey: options.secretAccessKey,
      },
      // Retries are handled a layer up (StorageOperationRunner) so that the
      // circuit breaker and audit trail see every attempt. Leaving the SDK's
      // own retries on would multiply attempts against a dead endpoint.
      maxAttempts: 1,
    });
  }

  /** Path prefix is applied here and nowhere else, so callers never see it. */
  private full(key: string): string {
    return `${this.prefix}${key.replace(/^\/+/, '')}`;
  }

  async save(buffer: Buffer, key: string, mimeType: string): Promise<SaveResult> {
    const checksum = createHash('sha256').update(buffer).digest('hex');
    await this.client.send(
      new PutObjectCommand({
        Bucket: this.bucket,
        Key: this.full(key),
        Body: buffer,
        ContentType: mimeType,
        // Immutable keys (a replace writes a new key) make a long max-age
        // correct without cache-busting query strings.
        CacheControl: 'public, max-age=31536000, immutable',
      })
    );
    return { url: this.publicUrl(key), sizeBytes: buffer.length, storageKey: key, checksum };
  }

  async read(key: string): Promise<Buffer> {
    const res = await this.client.send(
      new GetObjectCommand({ Bucket: this.bucket, Key: this.full(key) })
    );
    const body = res.Body as { transformToByteArray?: () => Promise<Uint8Array> } | undefined;
    if (!body?.transformToByteArray) throw new Error(`Empty body for ${key}`);
    return Buffer.from(await body.transformToByteArray());
  }

  async delete(key: string): Promise<void> {
    await this.client.send(new DeleteObjectCommand({ Bucket: this.bucket, Key: this.full(key) }));
  }

  async deleteMany(keys: string[]): Promise<void> {
    if (!keys.length) return;
    // DeleteObjects caps at 1000 keys per call; purging a deleted merchant
    // will exceed that.
    for (let i = 0; i < keys.length; i += 1000) {
      const batch = keys.slice(i, i + 1000);
      await this.client.send(
        new DeleteObjectsCommand({
          Bucket: this.bucket,
          Delete: { Objects: batch.map(k => ({ Key: this.full(k) })), Quiet: true },
        })
      );
    }
  }

  async exists(key: string): Promise<boolean> {
    try {
      await this.client.send(new HeadObjectCommand({ Bucket: this.bucket, Key: this.full(key) }));
      return true;
    } catch (err) {
      if (isNotFound(err)) return false;
      throw err;
    }
  }

  async getMetadata(key: string): Promise<ObjectMetadata> {
    const res = await this.client.send(
      new HeadObjectCommand({ Bucket: this.bucket, Key: this.full(key) })
    );
    return {
      sizeBytes: Number(res.ContentLength ?? 0),
      mimeType: res.ContentType ?? 'application/octet-stream',
      etag: res.ETag?.replace(/"/g, ''),
      lastModified: res.LastModified,
    };
  }

  async list(prefix: string, cursor?: string): Promise<ListPage> {
    const res = await this.client.send(
      new ListObjectsV2Command({
        Bucket: this.bucket,
        Prefix: this.full(prefix),
        ContinuationToken: cursor,
        MaxKeys: 1000,
      })
    );
    const strip = this.prefix.length;
    return {
      keys: (res.Contents ?? []).map(o => (o.Key ?? '').slice(strip)).filter(Boolean),
      nextCursor: res.IsTruncated ? res.NextContinuationToken : undefined,
    };
  }

  publicUrl(key: string): string {
    if (!this.publicBase) {
      // No public base configured is a setup error, not a runtime one — but
      // returning a broken relative path beats throwing inside a render path.
      return `/${this.full(key)}`;
    }
    return `${this.publicBase}/${this.full(key)}`;
  }

  createDownloadUrl(key: string, ttlSeconds: number): Promise<string> {
    return getSignedUrl(
      this.client,
      new GetObjectCommand({
        Bucket: this.bucket,
        Key: this.full(key),
        // Documents are downloads, not renders — and never inline HTML/SVG
        // executing on our origin.
        ResponseContentDisposition: 'attachment',
      }),
      { expiresIn: ttlSeconds }
    );
  }

  async createUploadUrl(key: string, mimeType: string, maxBytes: number): Promise<PresignedUpload> {
    const expiresIn = 300;
    // ContentLength is signed into the URL: without it a presigned PUT is an
    // open upload endpoint, and the size the client declared at reserve time
    // would be unenforceable.
    const url = await getSignedUrl(
      this.client,
      new PutObjectCommand({
        Bucket: this.bucket,
        Key: this.full(key),
        ContentType: mimeType,
        ContentLength: maxBytes,
      }),
      { expiresIn }
    );
    return {
      url,
      method: 'PUT',
      headers: { 'content-type': mimeType, 'content-length': String(maxBytes) },
      expiresAt: new Date(Date.now() + expiresIn * 1000),
    };
  }

  /**
   * Full round trip: list → write → read back → delete, then fetch the probe
   * object over the *public* base URL.
   *
   * The public fetch is the part that matters and the part a server-side-only
   * check would miss: a bucket whose credentials work perfectly but which
   * serves 403 to anonymous readers cannot host a storefront, and the merchant
   * needs to learn that during setup rather than from broken product images.
   */
  async testConnection(): Promise<ConnectionProbeResult> {
    const result: ConnectionProbeResult = {
      ok: false,
      canList: false,
      canWrite: false,
      canRead: false,
      canDelete: false,
      publicReadOk: false,
      state: 'config_error',
    };
    const probeKey = `.platform-probe/${randomUUID()}.txt`;
    const probeBody = Buffer.from(`forgestack storage probe ${new Date().toISOString()}`);

    try {
      await this.list('', undefined);
      result.canList = true;

      await this.save(probeBody, probeKey, 'text/plain');
      result.canWrite = true;

      const readBack = await this.read(probeKey);
      result.canRead = readBack.length === probeBody.length;

      if (this.publicBase) {
        try {
          const res = await fetch(this.publicUrl(probeKey), { method: 'GET' });
          result.publicReadOk = res.ok;
          if (!res.ok) {
            result.message = `Public URL returned ${res.status}. The bucket must allow anonymous reads for storefront images.`;
          }
        } catch (err) {
          result.message = `Public URL unreachable: ${errorMessage(err)}`;
        }
      } else {
        result.message = 'No public base URL configured — storefront images cannot be served.';
      }

      await this.delete(probeKey);
      result.canDelete = true;

      result.ok = result.canList && result.canWrite && result.canRead && result.canDelete && result.publicReadOk;
      result.state = result.ok ? 'connected' : 'degraded';
      return result;
    } catch (err) {
      result.state = classifyError(err);
      result.message = errorMessage(err);
      // Best-effort cleanup: a probe object left behind after a partial
      // failure is litter in the merchant's bucket, not ours.
      if (result.canWrite && !result.canDelete) {
        await this.delete(probeKey).catch(() => undefined);
      }
      return result;
    }
  }
}

function statusCode(err: unknown): number | undefined {
  const meta = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata;
  return meta?.httpStatusCode;
}

function errorName(err: unknown): string {
  return (err as { name?: string; Code?: string })?.name ?? (err as { Code?: string })?.Code ?? '';
}

export function isNotFound(err: unknown): boolean {
  const name = errorName(err);
  return name === 'NotFound' || name === 'NoSuchKey' || statusCode(err) === 404;
}

/**
 * Maps a provider error onto the health state the merchant sees.
 *
 * Kept exported and pure so the health cron and the upload path classify the
 * same failure identically — a merchant should not see "provider unavailable"
 * on one screen and "auth failed" on another for one revoked key.
 */
export function classifyError(err: unknown): StorageHealthState {
  const name = errorName(err);
  const status = statusCode(err);

  if (name === 'NoSuchBucket' || name === 'NotFound') return 'bucket_not_found';
  if (name === 'InvalidAccessKeyId' || name === 'SignatureDoesNotMatch' || status === 401) {
    return 'auth_failed';
  }
  if (name === 'AccessDenied' || status === 403) return 'permission_denied';
  if (status !== undefined && status >= 500) return 'provider_unavailable';
  if (name === 'TimeoutError' || name === 'NetworkingError' || name === 'ECONNREFUSED') {
    return 'provider_unavailable';
  }
  return 'config_error';
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/** Transient failures are worth retrying; a wrong key never becomes right. */
export function isTransient(err: unknown): boolean {
  const state = classifyError(err);
  return state === 'provider_unavailable' || statusCode(err) === 429;
}
